/* eslint-disable no-trailing-spaces */
/* eslint-disable eqeqeq */
/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/
import { IModuleProps, INodeProps } from '@msdyn365-commerce-modules/utilities';
import { Carousel } from '@msdyn365-commerce/components';
import classnames from 'classnames';
import * as React from 'react';
import { PromoBannerCloseButton, PromoBannerLinks, PromoBannerText } from './components';
import { IBannerMessagesData, IIePromoBannerProps } from './ie-promo-banner.props.autogenerated';
import { IIePromoBannerData } from './ie-promo-banner.data';

export interface IPromoBannerState {
    animating: boolean;
    isVisible: boolean;
    activeIndex: number;
}

export interface IPromoBannerViewProps extends IIePromoBannerProps<IIePromoBannerData>, IPromoBannerState {
    promoBannerProps: IModuleProps;
    contentProps: INodeProps ;
    bannerMessages: IBannerMessagesData[];
    PromoBannerTextProps: INodeProps;
    PromoBannerLinksProps: INodeProps;
    CarouselProps: INodeProps;
    closeButton?: React.ReactNode;
    className: string;
    onDismiss(): void;
    promoBannerMessages: IBannerMessagesData[];
}

var bannerMessages: IBannerMessagesData[] = [];

/**
 *
 * PromoBannerModule component
 * @extends {React.PureComponent<IIePromoBannerProps<IPromoBannerState>>}
 */
class PromoBanner extends React.PureComponent<IIePromoBannerProps<IIePromoBannerData>, IPromoBannerState> {
    public state: IPromoBannerState = {
        animating: false,
        isVisible: true,
        activeIndex: 0
    };

    public render(): JSX.Element | null {
        var { dismissEnabled, interval, hideFlipper, className = '' } = this.props.config;
        const { closeButtonAriaLabel, closeButtonLabel } = this.props.resources;
        const { isVisible, activeIndex } = this.state;

        if (!isVisible) {
            return null;
        }
        if(bannerMessages.length == 0){               
            bannerMessages?.push({
                "text": this.props.app.config.eventTitle
            });   
        }
        const viewProps = {
            // Context for telemetry and raw data for C1 to do customization
            // Including computed data, state data and methods
            ...this.props,
            className: classnames('ms-promo-banner', className),
            isVisible, // state data
            onDismiss: this._onDismiss, // methods

            promoBannerProps: { className: classnames('ms-promo-banner', className), moduleProps: this.props, role: 'banner' },
            PromoBannerTextProps: {
                tag: PromoBannerText
            },
            PromoBannerLinksProps: {
                tag: PromoBannerLinks
            },
            contentProps: { className: 'ms-promo-banner__content' },
            bannerMessages: bannerMessages,
            activeIndex: activeIndex,
            CarouselProps: {
                tag: Carousel,
                className: 'ms-promo-banner__carousel',
                activeIndex: activeIndex,
                next: this._next,
                previous: this._previous,
                onIndicatorsClickHandler: this._goToIndex,
                interval: interval,
                hideFlipper: hideFlipper,
                autoplay: false
            },
            closeButton: dismissEnabled && <PromoBannerCloseButton {...{ onClose: this._onDismiss, label: closeButtonLabel, closeButtonAriaLabel }} />
        };

        return this.props.renderView(viewProps) as React.ReactElement;
    }

    private _next = (): void => {
        const nextIndex = this._isLastItem() ? 0 : this.state.activeIndex + 1;
        this._goToIndex(nextIndex);
    };

    private _previous = (): void => {
        const nextIndex = this._isFirstItem() ? (this.props.config.bannerMessages ? this.props.config.bannerMessages.length - 1 : 0) : this.state.activeIndex - 1;
        this._goToIndex(nextIndex);
    };

    private _goToIndex = (index: number): void => {
        if (this.state.animating) {
            return;
        }
        this.setState({ activeIndex: index });
    };

    private _isFirstItem = () => this.state.activeIndex === 0;

    private _isLastItem = () => this.props.config.bannerMessages && this.state.activeIndex === this.props.config.bannerMessages.length - 1;

    private _onDismiss = (): void => {
        this.setState({ isVisible: false });
    };
}

export default PromoBanner;
